export const name = 'rnDevice';

/**
 * @ngdoc service
 * @name rhino.components.device.service:rnDevice
 *
 * @description Rhino Device service, provides methods to interact with devices.
 */
export default function rnDevice($q, $log, $filter, rnHttp, rnSession, rnPusher, rnRecordFactory, rnRecordCollectionFactory, systemStatisticsService, $window) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  const AAA_SERVICE_NAME = 'aaa';
  const SYS_STATS_SERVICE_NAME = 'system_statistics';
  const ORG_DEVICES_PATH = 'organizations/devices/';
  const ORG_DEVICES_WITH_CONTEXT_PATH = 'organizations/devices/v2';
  const DEVICE_MODEL_PATH = 'devices/modeltypes/';
  const ORG_DEVICE_MODEL_PATH = 'organizations/devices/modeltypes/';

  var serialNumberRequiredTypeList = ['LatexPrinter', 'DesignjetPrinter', 'TextilePrinter', 'PageWideXLPrinter', 'IndigoPress', 'IHPSPress', 'ScitexPress', 'FinishingDevice'];

  var service = {
    setupPusher: setupPusher,
    getDeviceModels: getDeviceModels,
    getOrganizationDeviceModels: getOrganizationDeviceModels,
    addDevice: addDevice,
    updateDevice: updateDevice,
    getById: getById,
    getAll: getAll,
    getAllWithContext: getAllWithContext,
    addDisplayInfo: addDisplayInfo,
    getImagePath: getImagePath,
    getScaledImagePath: getScaledImagePath,

    // Is anybody calling this directly? or is it just exposed here to enable tests? It is called by the .getAll function
    find: find,

    // We would like to obsolete these
    newDevice: newDevice,
    getDevicesForTable: getDevicesForTable,
    getDevicesForWidgets: getDevicesForWidgets,
    getDeviceStats: getDeviceStats
  };

  return service;

  ////////////////

  /**
   * @ngdoc function
   * @name setupPusher
   * @methodOf rhino.components.device.service:rnDevice
   * @param {string} channelName of the channel to bind to
   * @description Binds a callback function to a channel event.
   * @param {function} callback for pusher to call
   * @param {object} scope - so that the callback is unbound on scope destruction
   */
  function setupPusher(callback, scope) {
    if (callback && scope) {
      rnSession.userContext()
      .then(function (context) {
        if (context !== undefined && context !== null) {
          rnPusher.bind('private-' + context.id + '-DeviceStatistics', 'NewDeviceStatistic', callback, scope)
          .catch(function(error) {
            $log.warn('Unable to setup pusher: ', error);
          });
        }
        else {
          $log.warn('Unable to get user context to setup pusher');
        }
      })
      .catch(function(error) {
        $log.warn('Unable to get user context to setup pusher: ', error);
      });
    }
    else {
      $log.warn('callback and scope are required. Pusher not setup');
    }
  }

  // Get all device models
  function getDeviceModels() {
    return doGet(AAA_SERVICE_NAME, DEVICE_MODEL_PATH).then(function(data) {
      data = changeDeviceTypeHashtoList(data);
      return data;
    });
  }

  // Get all device models that are avaialable for this organization
  // For example, some unreleased device models may not be available to this organization and will not be included
  function getOrganizationDeviceModels() {
    return doGet(AAA_SERVICE_NAME, ORG_DEVICE_MODEL_PATH).then(function(data) {
      data = changeDeviceTypeHashtoList(data);
      return data;
    });
  }

  // The upstream caller of this function is expected to have already injected it into the device passed in.
  function addDevice(device, url) {
    return doAaaPost(url, device);
  }

  // The caller is expected to strip _links and internal nodes before passing in.
  function updateDevice(device, url) {
    return doAaaPut(url, device);
  }

  function getById(id) {
    return doGet(AAA_SERVICE_NAME, `organizations/devices/${id}`).then(function(data) {
      let device = createRecord(data);
      device = addDisplayInfo(device);
      return device;
    }).catch(function(err) {
      return $q.reject(err);
    });
  }

  function getAll(offset, limit) {
    return getAllWithContext({offset: offset, limit: limit});
  }

  function getAllWithContext(params) {
    let config = {params: params};

    return doGet(AAA_SERVICE_NAME, ORG_DEVICES_WITH_CONTEXT_PATH, config)
      .then(function(data) {
        let combinedDeviceRecords = [];

        // Iterate through each context and create Rhino records for the devices, and also copy the devices
        // into a single combined devices array
        _.forEach(data.devicesWithContext, function(deviceCollection) {
          let deviceRecords;
          let deviceRecordsCollection;

          deviceRecords = _.map(deviceCollection.devices, createRecord);

          // Add to a single "devices" array which is all of the devices from each context combined together
          combinedDeviceRecords = combinedDeviceRecords.concat(deviceRecords);

          deviceCollection.devices = deviceRecords;
          deviceRecordsCollection = rnRecordCollectionFactory.create(deviceCollection);

          deviceRecordsCollection = addDisplayInfoToList(deviceRecordsCollection);

          deviceCollection.devices = deviceRecordsCollection.devices;
        });

        let combinedDeviceRecordsCollection = {devices: combinedDeviceRecords};
        data.devices = addDisplayInfoToList(combinedDeviceRecordsCollection).devices;

        let deviceWithContextRecords = _.map(data.devicesWithContext, createRecord);
        data.devicesWithContext = deviceWithContextRecords;

        let dataCollection = rnRecordCollectionFactory.create(data);

        return dataCollection;
      })
      .catch(function(err) {
        return $q.reject(err);
      });
  }

  function isPrintBeatRealTimeDevice(device) {
    switch (device.type) {
      case 'LatexPrinter':
      case 'TextilePrinter':
      case 'DesignjetPrinter':
      case 'PageWideXLPrinter':
      case 'ScitexPress':
        return true;
      case 'IHPSPress':
        return false;
      case 'IndigoPress':
        switch (device.model) {
          case 'HP Indigo 10000 Digital Press':
          case 'HP Indigo 12000 Digital Press':
          case 'HP Indigo 13000 Digital Press':
          case 'HP Indigo 20000 Digital Press':
          case 'HP Indigo 30000 Digital Press':
          case 'HP Indigo 50000 Digital Press':
          case 'HP Indigo s2000 Digital Press':
          case 'HP Indigo ws2000 Digital Press':
          case 'HP Indigo w3200 Digital Press':
          case 'HP Indigo w3250 Digital Press':
          case 'hp indigo press 5500':
          case 'HP Indigo 5600 Digital Press':
          case 'HP Indigo 5900 Digital Press':
          case 'HP Indigo WS6000 Digital Press':
          case 'HP Indigo WS6000p Digital Press':
          case 'HP Indigo WS6600 Digital Press':
          case 'HP Indigo WS6600p Digital Press':
          case 'HP Indigo WS6800 Digital Press':
          case 'HP Indigo WS6800p Digital Press':
          case 'HP Indigo WS6900 Digital Press':
          case 'HP Indigo 6900 Digital Press':
          case 'HP Indigo 7000 Digital Press':
          case 'HP Indigo 7500 Digital Press':
          case 'HP Indigo 7600 Digital Press':
          case 'HP Indigo 7800 Digital Press':
          case 'HP Indigo 7900 Digital Press':
          case 'HP Indigo 7eco Digital Press':
          case 'HP Indigo Series5 Digital Press':
          case 'HP Indigo 8000 Digital Press':
          case 'HP Indigo 5r Digital Press':
          case 'HP Indigo 6r Digital Press':
          case 'HP Indigo 7r Digital Press':
          case 'HP Indigo 10r Digital Press':
          case 'HP Indigo 10000HD Digital Press':
          case 'HP Indigo 12000HD Digital Press':
          // case 'HP Indigo Ayala':
          // case 'HP Indigo Ayala HD':
            return true;
          default:
            return false;
        }
    }
  }

  function find(params) {
    let config = {
      params: params
    };

    return doGet(AAA_SERVICE_NAME, ORG_DEVICES_PATH, config).then(function(deviceCollection) {
      let deviceRecords;
      let deviceRecordsCollection;

      deviceRecords = _.map(deviceCollection.devices, createRecord);
      deviceCollection.devices = deviceRecords;
      deviceRecordsCollection = rnRecordCollectionFactory.create(deviceCollection);

      deviceRecordsCollection = addDisplayInfoToList(deviceRecordsCollection);

      return deviceRecordsCollection;
    }).catch(function(err) {
      $q.reject(err);
    });
  }

  // We would like to obsolete this call (it isn't used in skyline).
  function getDeviceStats(deviceId) {
    var path = 'devices/' + deviceId + '/latest';

    return doGet(SYS_STATS_SERVICE_NAME, path);
  }

  /**
   * Get a bunch of device items from the given url
   * We would like to obsolete this call (it isn't used in skyline).
   * @returns {*}  An array of devices.
   */
  function getDevicesForWidgets(offset, limit) {
    var devices;

    return getDevicesForTable(offset, limit)
      .then(function (data) {
        devices = _.filter(data.devices, function (entry) {
          return (entry.type !== 'PSPAgent');
        });
        angular.forEach(devices, function (device, key) {
          device.id = device.type;    // Why are we setting the id to the type?
          systemStatisticsService.getDeviceStats(device.id, device);
        });
        return devices;
      });
  }

  // This used to take the standard data from the server, then replace the type and model fields with their
  // localized values. But now it is no different than calling getAll.
  // We would like to obsolete this call (it isn't used in skyline).
  function getDevicesForTable(offset, limit) {
    return getAll(offset, limit);
  }

  // Used to get a new, empty device object from the server.  Not currently implemented by AAA so not used ATM.
  // We would like to obsolete this call (it isn't used in skyline).
  function newDevice(url) {

    return doGet(AAA_SERVICE_NAME, url);
  }

  //
  // private functions
  //

  function addDisplayInfoToList(deviceList) {
    var devicesWithDisplayInfo = {};
    angular.copy(deviceList, devicesWithDisplayInfo);
    _.forEach(devicesWithDisplayInfo.devices, function(device) {
      addDisplayInfo(device);
    });

    return devicesWithDisplayInfo;
  }

  function addDisplayInfo(device) {
    if (device && device.internal) {
      device.internal.isPBRTSupported = isPrintBeatRealTimeDevice(device);
      // Display info such as loc keys and image paths
      let catalogPrefix = 'devices.devices.';
      device.internal.display = {};
      let display = device.internal.display;

      device.internal.loc = {};
      let loc = device.internal.loc;

      if (device.type) {
        display.typeKey = sanitizeKey(catalogPrefix + device.type);
        loc.type = $filter('L10N')('devices.devices.' + sanitizeKey(device.type));

      }

      if (device.model) {
        display.modelKey = sanitizeKey(catalogPrefix + device.model);
        display.imagePath = getImagePath(device.model);
        display.scaledImagePath = getScaledImagePath(device.model);
        loc.model = $filter('L10N')('devices.devices.' + sanitizeKey(device.model));
      }

      // Take the standard data from the server and localize them here.
      // This allows the visible strings to show up as search/filter results when
      // using an Angular filter.
      loc.active = (device.active)?
        $filter('L10N')('devices.devices.Device_Active') : $filter('L10N')('devices.devices.Device_Not_Active');

      if (device.parentId !== undefined) {
        loc.jobSubmission = $filter('L10N')('devices.devices.Device_Use_Parent');
      } else if (device.jobDestination !== undefined) {
        loc.jobSubmission = $filter('L10N')('devices.devices.Device_Direct');
      } else {
        loc.jobSubmission = $filter('L10N')('devices.devices.Device_Not_Configured');
      }
    }

    return device;
  }

  function getImagePath(model) {
    let fileKey = sanitizeKey(model);
    return 'https://cdn.printos.com/images/devices/general/' + $window.encodeURIComponent(fileKey) + '.png';
  }

  function getScaledImagePath(model) {
    let fileKey = sanitizeKey(model);
    return 'https://cdn.printos.com/images/devices/scaled/' + $window.encodeURIComponent(fileKey) + '.png';
  }

  function createRecord(data) {
    let device = rnRecordFactory.create(data);
    // JVM - hack - set id attribute for a consistent model.
    // This should be changed at the server.
    device.internal.idName = 'deviceId';
    return device;
  }

  // Some of the elements (i.e. model) in the AAA database have spaces in them, which are not suitable as localization
  // keys. Change the key appropriately.
  function sanitizeKey(key) {
    key = key.replace(/ /g, '_');
    return key;
  }

  // Massage the data a bit to provide more hints to the UI on when to show or require fields.
  // This routine takes the data from the server and applies some business logic to it, adding a
  // couple fields that are useful to the UI.
  function changeDeviceTypeHashtoList(typeModels) {
    let catalogPrefix = 'devices.devices.';
    var deviceTypeList= [];
    angular.forEach(typeModels, function (models, key) {
      var deviceType = {};
      deviceType.name= key;
      deviceType.nameKey = catalogPrefix + sanitizeKey(key);
      deviceType.selfProvisionable= isSelfProvisionable(models);
      deviceType.serialNumRequired = isSerialNumRequired(key);
      angular.forEach(models, function (model, modelKey) {
        model.modelNameKey = catalogPrefix + sanitizeKey(model.modelName);
      });
      deviceType.models = models;
      deviceTypeList.push(deviceType);
    });
    return deviceTypeList;
  }

  // If all models of a device type are self-provisionable, indicate that the
  // entire device type is self-provisionable so we can hide it from the add dialog.
  // The end user should not be able to manually add a self-provisionable device, and AAA will block it.
  function isSelfProvisionable(models) {
    return !_.any(models, 'selfProvisionable', false);
  }

  function isSerialNumRequired(name) {
    return _.includes(serialNumberRequiredTypeList, name);
  }

  function doGet(service, path, config = {}) {
    path = cleanPath(path);

    return rnHttp.get(service, path, config)
      .then(function(data) {
        return data.data;
      })
      .catch(function(reason) {
        return $q.reject(reason);
      });
  }

  function doAaaPost(path, content) {
    path = cleanPath(path);
    return rnHttp.post(AAA_SERVICE_NAME, path, content);
  }

  function doAaaPut(path, content) {
    path = cleanPath(path);
    return rnHttp.put(AAA_SERVICE_NAME, path, content);
  }

  function doAaaDeleteWithContent(path, content) {
    path = cleanPath(path);
    return rnHttp.deleteWithBody(AAA_SERVICE_NAME, path, content);
  }

  // DJL - Some HAL implementation for REST responses has already begun on the server. However, SOME of the
  // link vals are returning absolute URLs. This is necessary until we formally outline (and implement)
  // our approach to JSON/HAL on both the client and server.
  // TODO remove when we no longer have to deal with both absolute and relative URLs
  function cleanPath(path) {
    // Support v1, v2...versions optimistically through v9 by removing version out of replace and adjusting +2
    if (path.indexOf('/api/aaa/v') >= 0) {
      let start = path.indexOf('organizations');
      let end = path.length+2;
      return path.substring(start, end);
    }
    else {
      return path;
    }
  }
}



// WEBPACK FOOTER //
// ../~/@printos/rhino/src/components/device/device.service.js